/**
 * @fileoverview Universal logging utility that provides environment-aware logging to both console and Datadog.
 *
 * NB: The intention of this logger is to automatically handle the complexity of routing logs appropriately based on environment:
 * As of 04.06.2025, we have decided to route _ALL_ logs to datdog, so this utility won't do much good for now. If we decide to
 * limit console logs down to what we decide to send programatically, this utility will be handy. But for now, stick with console.log/warn/error.
 * - Development: All logs go to console only (no Datadog noise)
 * - Production: Logs go to both console and Datadog for monitoring
 *
 * The logger assumes Datadog is initialized in entry.client.ts and checks for the global
 * `__DATADOG_INITIALIZED__` flag before attempting to send logs.
 *
 * @example
 * ```typescript
 * import { logger } from "~/utils/logger";
 *
 * logger.info("User logged in", { userId: 123 });
 * logger.error("API failed", { endpoint: "/users", status: 500 });
 * logger.debug("Component state changed", { newState }); // Dev only
 *
 * // Performance tracking
 * const start = logger.perf.start("expensive_operation");
 * // ... do work ...
 * logger.perf.end("expensive_operation", start);
 * ```
 */

/**
 * Available log levels for the logger.
 * - `info`: General information logs
 * - `warn`: Warning messages that don't break functionality
 * - `error`: Error messages for actual problems
 * - `debug`: Verbose debugging information (development only)
 */
type LogLevel = "info" | "warn" | "error" | "debug";

/**
 * Context object that can be attached to any log for additional metadata.
 * This gets serialized and sent along with the log message to provide debugging context.
 */
interface LogContext {
  [key: string]: unknown;
}

const isDev = import.meta.env.MODE === "development";
const isProd = import.meta.env.PROD;

/**
 * Attempts to send a log to Datadog using dynamic imports.
 * Fails gracefully if Datadog is not available or not initialized.
 *
 * @param level - The log level (debug logs are never sent to Datadog)
 * @param message - The log message
 * @param context - Optional context object with additional metadata
 */
const logToDatadogBrowserLogs = async (
  level: Exclude<LogLevel, "debug">,
  message: string,
  context?: LogContext,
) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const { datadogLogs } = await import("@datadog/browser-logs");
    datadogLogs.logger[level](message, context);
  } catch (error) {
    console.warn("Failed to send log to Datadog:", error);
  }
};

/**
 * Determines whether a log should be forwarded to Datadog based on environment and log level.
 *
 * **Environment Strategy:**
 * - **Development**: Never send to Datadog to avoid noise from local debugging, errors during development, and test data
 * - **Production**: Send meaningful logs to Datadog for monitoring and alerting
 *
 * **Log Level Strategy:**
 * - **error/warn/info**: Sent to Datadog in production for monitoring and troubleshooting
 * - **debug**: Never sent to Datadog (too verbose, only useful during active development)
 *
 * This keeps Datadog dashboards clean and focused on real production issues while allowing
 * developers to freely log/debug locally without affecting monitoring data.
 *
 * @param level - The log level being evaluated
 * @returns `true` if the log should be sent to Datadog, `false` otherwise
 *
 * @example
 * ```typescript
 * shouldSendToDatadog("error") // false in dev, true in prod
 * shouldSendToDatadog("debug") // always false
 * ```
 */
const shouldLogToDatadog = (level: LogLevel): boolean => {
  if (isProd && (level === "error" || level === "warn" || level === "info")) {
    return true;
  }

  return false;
};

/**
 * Universal logger that provides environment-aware logging to console and Datadog.
 *
 * All methods log to console immediately for debugging. In production, appropriate logs
 * are also forwarded to Datadog for monitoring and alerting.
 *
 * Name `tLogger` to not confuse it with `logger` from the million exported `logger`s among our npm packages.
 * Short for TopasLogger, of course.
 */
export const tLogger = {
  /**
   * Log general information messages.
   * In production, send to datadog. Else console.log
   *
   * @param message - The log message
   * @param context - Optional context object with additional metadata
   *
   * @example
   * ```typescript
   * logger.info("User session started", { userId: 123, sessionId: "abc" });
   * ```
   */
  info: (message: string, context?: LogContext) => {
    if (shouldLogToDatadog("info")) {
      logToDatadogBrowserLogs("info", message, context);
    } else {
      console.log(message, context);
    }
  },

  /**
   * Log warning messages for potentially problematic situations.
   * Always logs to console. In production, also sends to Datadog.
   *
   * @param message - The warning message
   * @param context - Optional context object with additional metadata
   *
   * @example
   * ```typescript
   * logger.warn("API rate limit approaching", { usage: 95, limit: 100 });
   * ```
   */
  warn: (message: string, context?: LogContext) => {
    if (shouldLogToDatadog("warn")) {
      logToDatadogBrowserLogs("warn", message, context);
    } else {
      console.warn(message, context);
    }
  },

  /**
   * Log error messages for actual problems and failures.
   * Always logs to console. In production, also sends to Datadog for alerting.
   *
   * @param message - The error message
   * @param context - Optional context object with additional metadata (often includes error details)
   *
   * @example
   * ```typescript
   * logger.error("Payment processing failed", {
   *   orderId: "123",
   *   error: error.message,
   *   userId: 456
   * });
   * ```
   */
  error: (message: string, context?: LogContext) => {
    if (shouldLogToDatadog("error")) {
      logToDatadogBrowserLogs("error", message, context);
    } else {
      console.error(message, context);
    }
  },

  /**
   * Log verbose debugging information.
   * Only logs to console in development. Never sent to Datadog.
   * Use this for temporary debugging or detailed state information.
   *
   * @param message - The debug message
   * @param context - Optional context object with additional metadata
   *
   * @example
   * ```typescript
   * logger.debug("Component re-rendered", { renderCount: 5, props: {...} });
   * ```
   */
  debug: (message: string, context?: LogContext) => {
    if (isDev) {
      console.log(`🐛 [DEBUG] ${message}`, context);
    }
  },

  /**
   * Performance tracking utilities for measuring operation duration.
   * Useful for identifying slow operations and bottlenecks.
   */
  perf: {
    /**
     * Start timing an operation.
     * In development, also starts a console timer for visual feedback.
     *
     * @param operation - Name of the operation being timed
     * @returns Start timestamp that should be passed to `perf.end()`
     *
     * @example
     * ```typescript
     * const start = logger.perf.start("data_processing");
     * ```
     */
    start: (operation: string) => {
      if (isDev) {
        console.time(`⚡ ${operation}`);
      }
      return window.performance.now();
    },

    /**
     * End timing an operation and log the duration.
     * Automatically logs slow operations (>1000ms) as warnings to Datadog in production.
     *
     * @param operation - Name of the operation (should match the one passed to `start()`)
     * @param startTime - Start timestamp returned from `perf.start()`
     * @param context - Optional context object with additional metadata
     *
     * @example
     * ```typescript
     * const start = logger.perf.start("api_call");
     * const result = await fetchData();
     * logger.perf.end("api_call", start, { recordCount: result.length });
     * ```
     */
    end: (operation: string, startTime: number, context?: LogContext) => {
      const duration = window.performance.now() - startTime;
      const message = `Performance: ${operation} took ${duration.toFixed(2)}ms`;

      if (isDev) {
        console.timeEnd(`⚡ ${operation}`);
        console.log(message, context);
      }

      // Log slow operations to Datadog as potential performance issues
      if (duration > 1000 && shouldLogToDatadog("warn")) {
        logToDatadogBrowserLogs("warn", `Slow Operation: ${operation}`, {
          ...context,
          duration,
          type: "performance",
        });
      }
    },
  },
};
